import FavouriteBrand from './FavouriteBrand';
import { useNavigate } from 'react-router-dom';
import { InterestAnalyticsItem, PartnerDetail, YSH_GTM_EVENTS } from '../types/types';
import FavouriteBrandAddMore from './FavouriteBrandAddMore';
import { useEffect, useState } from 'react';
import Sheet from 'react-modal-sheet';
import close from '../assets/images/icons/icon-close.svg';
import { useSaveSelectedPartnersMutation } from '../hooks/useSaveSelectedPartnersMutation';
import { pushToAnalytics } from '../utils/analytics';
import FilterPartnerWithFavorites from './FilterPartnerWithFavorites';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../constants/queryKeys';
import '../scss/favourite-brand-list.scss';

interface Props {
  selectedPartners: PartnerDetail[];
  setSelectedPartners: (partners: PartnerDetail[]) => void;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  placement: string;
}

const FavouriteBrandList: React.FC<Props> = ({
  selectedPartners,
  setSelectedPartners,
  isEditing,
  setIsEditing,
  placement
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [filterDisplaySelectedPartners, setFilterDisplaySelectedPartners] = useState<string>('');

  const saveSelectedPartners = useSaveSelectedPartnersMutation(
    filterDisplaySelectedPartners.split(','),
    handleSavedSelectedPartners,
    handleFailedToSaveSelectedPartners
  );

  async function handleSavedSelectedPartners() {
    setIsPopupOpen(false);
    setIsEditing(false);

    await queryClient.invalidateQueries({
      predicate: query => query.queryKey[0] === QUERY_KEYS.GET_SELECTED_PARTNERS
    });
  }

  function handleFailedToSaveSelectedPartners() {
    alert('Žao nam je, nismo uspeli da sačuvamo odabrane omiljene partnere');
  }

  const handleLinkClick = (id: string) => {
    navigate(`/ponude?partner=${id}`);
  };

  const handleRemoveClick = (id: string) => {
    const filteredPartners = selectedPartners.filter(partner => partner.id !== id);
    setSelectedPartners(filteredPartners);
  };

  const handleAddMoreClick = () => {
    pushToAnalytics({
      eventName: YSH_GTM_EVENTS.ADD_MORE_BRANDS,
      placement,
      data: {}
    });

    setIsPopupOpen(true);
  };

  const handleSaveFilteredListOfFavouriteBrands = () => {
    const partnersToBeSaved = selectedPartners.filter(partner => filterDisplaySelectedPartners.includes(partner.id));

    const items: InterestAnalyticsItem[] = partnersToBeSaved.map((p, idx) => {
      return {
        index: idx + 1,
        item_id: p.name,
        item_name: p.name,
        item_category: '',
        item_category2: ''
      };
    });

    pushToAnalytics({
      eventName: YSH_GTM_EVENTS.ADD_BRANDS,
      placement,
      data: { items }
    });
    setSelectedPartners(partnersToBeSaved);
    saveSelectedPartners.mutate();
  };

  useEffect(() => {
    if (selectedPartners) {
      setFilterDisplaySelectedPartners(selectedPartners.map(partner => partner.id).join(','));
    }
  }, [selectedPartners]);

  return (
    <>
      <section className="favourite-brand-list">
        {selectedPartners.map(partner => (
          <FavouriteBrand
            key={partner.id}
            partner={partner}
            handleLinkClick={handleLinkClick}
            handleRemoveClick={handleRemoveClick}
            isRemovable={isEditing}
          />
        ))}
        {isEditing && <FavouriteBrandAddMore handleAddMoreClick={handleAddMoreClick} />}
      </section>

      <Sheet isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <Sheet.Container className="ps-3 pe-3">
          <Sheet.Header>
            <div className="d-flex justify-content-end pt-3">
              <img
                className="cursor-pointer"
                width={25}
                height={25}
                src={close}
                alt="close modal"
                onClick={() => setIsPopupOpen(false)}
              />
            </div>
          </Sheet.Header>
          <Sheet.Content>
            <FilterPartnerWithFavorites
              userSelectedPartners={selectedPartners}
              setUserSelectedPartners={setSelectedPartners}
              handleSaveFilteredListOfFavouriteBrands={handleSaveFilteredListOfFavouriteBrands}
              placement="favourite_brands"
            />
          </Sheet.Content>
        </Sheet.Container>
      </Sheet>
    </>
  );
};

export default FavouriteBrandList;
